'use client';
import type { Program } from 'planby';
import { Epg, Layout, Theme, useEpg } from 'planby';
import { Channel } from 'planby/dist/Epg/helpers/interfaces';
import { createContext, RefObject, useEffect, useMemo, useRef } from 'react';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';
import { ChannelItem } from './ChannelItem';
import { ChannelRow } from './ChannelRow';
import { ProgramCard } from './ProgramCard';
import styles from './tvguide.module.sass';

// Temporary hack: always the current day
const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1); // Move to next day

const getModifiedPrograms = (programs: Program[]) =>
    programs.map((program) => {
        const programSince = new Date(program.since);
        const programTill = new Date(program.till);

        // Determine base date (today or tomorrow)
        const baseDate = program.isTomorrow ? tomorrow : today;

        // Preserve original time but update only the date
        const newSince = new Date(
            baseDate.getFullYear(),
            baseDate.getMonth(),
            baseDate.getDate(),
            programSince.getHours(),
            programSince.getMinutes(),
            programSince.getSeconds()
        );

        const newTill = new Date(
            baseDate.getFullYear(),
            baseDate.getMonth(),
            baseDate.getDate(),
            programTill.getHours(),
            programTill.getMinutes(),
            programTill.getSeconds()
        );

        return {
            ...program,
            since: newSince.toISOString(),
            till: newTill.toISOString(),
        };
    });

const customTheme: Theme = {
    primary: {
        600: 'var(--white-10)',
        900: 'var(--primary-colors-background)',
    },
    grey: { 300: '#d1d1d1' },
    white: '#fff',
    green: {
        300: 'var(--primary-colors-orange)',
    },
    loader: {
        teal: '#5DDADB',
        purple: '#3437A2',
        pink: '#F78EB6',
        bg: '#171923db',
    },
    scrollbar: {
        border: 'var(--primary-colors-background)',
        thumb: {
            bg: 'var(--primary-colors-background)',
        },
    },
    gradient: {
        blue: {
            300: '#fff',
            600: '#fff',
            900: '#fff',
        },
    },
    text: {
        grey: {
            300: '#a0aec0',
            500: '#718096',
        },
    },
    timeline: {
        divider: {
            bg: '#718096',
        },
    },
};

export const EpgContainerRefContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

type EPGProps = {
    onProgramSelect: (program: Program, channel: any) => void;
    onChannelSelect: (channelId: Channel) => void;
    channels: Channel[];
    programs: Program[];
    isProgramCellsWidthFixed?: boolean;
    isStorybook?: boolean;
    selectedProgram?: Program;
    isRTL?: boolean;
    favoriteIDs?: string[];
    lang?: string;
    liveTag?: string;
    beforeTag?: string;
    afterTag?: string;
    date: string;
    useRewindDuration?: (channel: any) => string;
    onFavoriteClick?: (channelId: string) => void | undefined;
    epgLocalization?: any;
    AddWatchListButton?: any;
    AddReminderButton?: any;
    reminderData?: any[];
    excludeIdFromUrl?: any;
    onPPVPurchaseClick?: (eventId: string) => void;
    // PPV configuration for pricing
    ppvEventConfig?: any;
    operators?: any;
    fetchPreviousProgramOnScroll?: (channelId: string) => void;
    fetchFutureProgramOnScroll?: (channelId: string) => void;
};

/**
 * @todo split into two components - one for default `planby` layout and one with fixed width program cells
 */
export const EPG = ({
    onProgramSelect,
    onChannelSelect,
    channels,
    programs,
    isProgramCellsWidthFixed,
    isStorybook,
    selectedProgram,
    isRTL,
    favoriteIDs,
    lang,
    liveTag,
    beforeTag,
    afterTag,
    date,
    useRewindDuration,
    onFavoriteClick,
    epgLocalization,
    AddWatchListButton,
    AddReminderButton,
    reminderData,
    excludeIdFromUrl,
    onPPVPurchaseClick,
    ppvEventConfig,
    operators,
    fetchPreviousProgramOnScroll,
    fetchFutureProgramOnScroll,
}: EPGProps) => {
    const isDesktop = useMediaQuery('(min-width: 1800px)');
    const isWideScreen = useMediaQuery('(min-width: 2200px)');
    const modifiedPrograms = isStorybook ? getModifiedPrograms(programs) : programs;
    const epgContainerRef = useRef<HTMLDivElement | null>(null);

    // Setting `since` to today 00:00 and `till` to today 23:59 so that channel row is taking the full EPG container
    // width when `isProgramCellsWidthFixed` is enabled
    const since = new Date();
    since.setHours(0, 0, 0);
    const till = new Date();
    till.setHours(23, 59, 59);

    // const minDate = useMemo(() => {
    //     if (!programs?.length) return new Date();

    //     return new Date(Math.min(...programs.map((p) => new Date(p.since).getTime())));
    // }, [programs]);
    // const since = minDate;

    // const till = useMemo(() => {
    //     if (!programs?.length) return new Date();

    //     return new Date(Math.max(...programs.map((p) => new Date(p.till).getTime())));
    // }, [programs]);

    const epg = channels.map((channel) => ({
        channelUuid: channel.uuid,
        id: channel.uuid,
        title: '<needed-to-satisfy-typescript>',
        description: '<needed-to-satisfy-typescript>',
        image: '<needed-to-satisfy-typescript>',
        since,
        till,
    }));

    const sidebarWidth = isWideScreen ? 154 : isDesktop ? 124 : 104;
    const { getEpgProps, getLayoutProps } = useEpg({
        sidebarWidth,
        channels: channels,
        epg: isProgramCellsWidthFixed ? epg : modifiedPrograms,
        itemHeight: isWideScreen ? 152 : isDesktop ? 122 : 102,
        theme: customTheme,
        isLine: false,
        isTimeline: false,
        // startDate: new Date(),
        // startDate: minDate, // ✅ FIXED
    });

    const layoutProps = getLayoutProps();

    useEffect(() => {
        if (!isProgramCellsWidthFixed) return;

        // TODO: check if this is a reliable selector?
        // For some reason, `Content` class is not present on staging. Because of `production` build mode?
        const programsGrid = document.querySelector<HTMLDivElement>("*[data-testid*='content']");

        if (programsGrid) {
            programsGrid.style.width = `calc(100% - ${sidebarWidth}px)`;
            programsGrid.style.insetInlineStart = `${sidebarWidth}px`;
        }
    }, [isProgramCellsWidthFixed, sidebarWidth]);

    return (
        <EpgContainerRefContext.Provider value={epgContainerRef}>
            <div className={styles.epgs} ref={epgContainerRef}>
                <div className={styles.epgContainer}>
                    {/* <Epg key={minDate.toISOString()} {...getEpgProps()}> */}
                    <Epg {...getEpgProps()}>
                        <Layout
                            {...layoutProps}
                            // renderTimeline={(props) => <TimeLine {...props} />}
                            renderChannel={({ channel }) => (
                                <ChannelItem
                                    key={channel.uuid}
                                    channel={channel}
                                    isSelected={selectedProgram?.channel?.channelID === channel?.id}
                                    onChannelSelect={onChannelSelect}
                                    selectedChannel={selectedProgram?.channelUuid ?? null}
                                    {...(favoriteIDs?.includes(channel.uuid) && { favorite: true })}
                                    // eslint-disable-next-line
                                    rewindDuration={useRewindDuration?.(channel) || ''}
                                    // TODO: pass correct locked status
                                    // isLockedChannel={true}
                                />
                            )}
                            renderProgram={({ program }) => {
                                return isProgramCellsWidthFixed ? (
                                    <ChannelRow
                                        /** @todo move props shared with ProgramCard into `defaultProps` */
                                        key={`${program.data.id}-${program.data.since}-${program.data.till}-${date}`}
                                        program={program}
                                        onProgramSelect={onProgramSelect}
                                        isRTL={isRTL ?? false}
                                        isBaseTimeFormat={false}
                                        dayWidth={layoutProps.dayWidth}
                                        programs={modifiedPrograms.filter((p) => {
                                            return p.channelUuid === program.data.channelUuid;
                                        })}
                                        selectedProgram={selectedProgram}
                                        lang={lang}
                                        liveTag={liveTag}
                                        beforeTag={beforeTag}
                                        afterTag={afterTag}
                                        isFavorite={favoriteIDs?.includes(program?.data.channelUuid)}
                                        onFavoriteClick={onFavoriteClick}
                                        epgLocalization={epgLocalization}
                                        AddWatchListButton={AddWatchListButton}
                                        AddReminderButton={AddReminderButton}
                                        reminderData={reminderData}
                                        excludeIdFromUrl={excludeIdFromUrl}
                                        date={date}
                                        onPPVPurchaseClick={onPPVPurchaseClick}
                                        ppvEventConfig={ppvEventConfig}
                                        operators={operators}
                                        fetchPreviousProgramOnScroll={fetchPreviousProgramOnScroll}
                                        fetchFutureProgramOnScroll={fetchFutureProgramOnScroll}
                                    />
                                ) : (
                                    <ProgramCard
                                        key={`${program.data.id}-${program.data.since}-${program.data.till}-${date}`}
                                        program={program}
                                        onProgramSelect={onProgramSelect}
                                        isRTL={false}
                                        isBaseTimeFormat={false}
                                        dayWidth={layoutProps.dayWidth}
                                        selectedProgram={selectedProgram}
                                    />
                                );
                            }}
                        />
                    </Epg>
                </div>
            </div>
        </EpgContainerRefContext.Provider>
    );
};
